import { cn } from '@/lib/cn'

interface ToggleFieldProps {
  id: string
  checked: boolean
  onChange: (checked: boolean) => void
  label: string
  description?: string
  disabled?: boolean
}

/**
 * A checkbox styled as a switch. The real `<input>` sits transparently over the
 * track, so it keeps its 44×24 hit area, stays in the tab order, and picks up
 * the global `:focus-visible` outline. Knob position — not just colour —
 * carries the state, so it survives forced-colors mode.
 */
export function ToggleField({
  id,
  checked,
  onChange,
  label,
  description,
  disabled,
}: ToggleFieldProps) {
  const descriptionId = description ? `${id}-description` : undefined

  return (
    <div className="flex items-start gap-3">
      <span className="relative mt-0.5 inline-flex h-6 w-11 shrink-0">
        <input
          id={id}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          aria-describedby={descriptionId}
          onChange={(event) => {
            onChange(event.target.checked)
          }}
          className="peer absolute inset-0 z-10 size-full cursor-pointer appearance-none rounded-full disabled:cursor-not-allowed"
        />
        <span
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute inset-0 rounded-full border transition-colors',
            checked
              ? 'border-primary-container/40 bg-primary-container'
              : 'border-glass-border bg-surface-container-high',
            disabled && 'opacity-50',
          )}
        />
        <span
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute top-1 size-4 rounded-full bg-white transition-[left] duration-200 motion-reduce:transition-none',
            checked ? 'left-6' : 'left-1',
          )}
        />
      </span>
      <span className="space-y-1">
        <label
          htmlFor={id}
          className="font-body text-body-md text-fg block cursor-pointer"
        >
          {label}
        </label>
        {description && (
          <p
            id={descriptionId}
            className="font-body text-body-sm text-fg-muted"
          >
            {description}
          </p>
        )}
      </span>
    </div>
  )
}
